import type {
	ConstantsAuditLogStruct,
	ConstantsSessionStruct,
} from "@gosaas/types";
import { useQuery } from "@tanstack/react-query";
import { apiClient, requestWithHandling } from "@/utils/api";
import { reportQueryError } from "@/utils/query-errors";

export const useFetchTeamAuditLogs = (teamId: string) => {
	const {
		data: auditLogs,
		isLoading: isLoadingAuditLogs,
		refetch: refetchAuditLogs,
	} = useQuery({
		queryKey: ["team-audit-logs", teamId],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsAuditLogStruct[]>(
					() => apiClient.api.securityTeamAuditLogsTeamIdList(teamId),
				);

				if (response.code === "SUCCESS") {
					return response.data;
				}

				throw new Error(response.message || "Failed to fetch audit logs");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load audit logs" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { auditLogs, isLoadingAuditLogs, refetchAuditLogs };
};

export const useFetchSessions = () => {
	const {
		data: sessions,
		isLoading: isLoadingSessions,
		refetch: refetchSessions,
	} = useQuery({
		queryKey: ["user-sessions"],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsSessionStruct[]>(
					() => apiClient.api.securitySessionsList(),
				);
				if (response.code === "SUCCESS") {
					return response.data ?? [];
				}
				throw new Error(response.message || "Failed to fetch sessions");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load active sessions" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { sessions, isLoadingSessions, refetchSessions };
};

export const useFetchUserAuditLogs = () => {
	const {
		data: auditLogs,
		isLoading: isLoadingAuditLogs,
		refetch: refetchAuditLogs,
	} = useQuery({
		queryKey: ["user-audit-logs"],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsAuditLogStruct[]>(
					() => apiClient.api.securityUserAuditLogsList(),
				);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch audit logs");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load audit logs" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { auditLogs, isLoadingAuditLogs, refetchAuditLogs };
};
